
// ABOUTME: Admin dashboard for viewing security audit logs and monitoring
// ABOUTME: Provides comprehensive security event tracking and analysis tools

import { useState } from 'react';
import { useSecurityAudit } from '@/hooks/useSecurityAudit';
import { useUserRoles } from '@/hooks/useUserRoles';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { AlertTriangle, Shield, Eye, Calendar, User, Activity } from 'lucide-react';
import { format } from 'date-fns';

export default function SecurityAuditDashboard() {
  const { isAdmin, canViewAuditLogs } = useUserRoles();
  const { auditLogs, isLoading } = useSecurityAudit();
  const [searchTerm, setSearchTerm] = useState('');
  const [filterAction, setFilterAction] = useState('all');
  const [filterSuccess, setFilterSuccess] = useState('all');

  if (!isAdmin() || !canViewAuditLogs()) {
    return (
      <div className="flex items-center justify-center min-h-64">
        <div className="text-center">
          <AlertTriangle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Access Denied</h3>
          <p className="text-gray-600">You don't have permission to view security audit logs.</p>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading security audit logs...</p>
        </div>
      </div>
    );
  }

  const filteredLogs = auditLogs.filter(log => {
    const matchesSearch = searchTerm === '' || 
      log.action.toLowerCase().includes(searchTerm.toLowerCase()) ||
      log.resource_type.toLowerCase().includes(searchTerm.toLowerCase()) ||
      log.user_id.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesAction = filterAction === 'all' || log.action === filterAction;
    const matchesSuccess = filterSuccess === 'all' || 
      (filterSuccess === 'success' && log.success) ||
      (filterSuccess === 'failure' && !log.success);
    
    return matchesSearch && matchesAction && matchesSuccess;
  });

  const getActionIcon = (action: string) => {
    switch (action) {
      case 'login_attempt':
        return <User className="w-4 h-4" />;
      case 'referral_creation':
        return <Activity className="w-4 h-4" />;
      case 'permission_denied':
        return <AlertTriangle className="w-4 h-4" />;
      default:
        return <Eye className="w-4 h-4" />;
    }
  };

  const getActionColor = (action: string, success: boolean) => {
    if (!success) return 'destructive';
    
    switch (action) {
      case 'login_attempt':
        return 'default';
      case 'referral_creation':
        return 'default';
      case 'permission_denied':
        return 'destructive';
      default:
        return 'secondary';
    }
  };

  const statsData = {
    totalEvents: auditLogs.length,
    successfulEvents: auditLogs.filter(log => log.success).length,
    failedEvents: auditLogs.filter(log => !log.success).length,
    uniqueUsers: new Set(auditLogs.map(log => log.user_id)).size,
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-2 mb-6">
        <Shield className="w-6 h-6 text-blue-600" />
        <h1 className="text-2xl font-bold text-gray-900">Security Audit Dashboard</h1>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">Total Events</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-gray-900">{statsData.totalEvents}</div>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">Successful Events</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">{statsData.successfulEvents}</div>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">Failed Events</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-red-600">{statsData.failedEvents}</div>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">Unique Users</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-blue-600">{statsData.uniqueUsers}</div>
          </CardContent>
        </Card>
      </div>

      {/* Show message if no audit logs available */}
      {auditLogs.length === 0 && (
        <Card>
          <CardContent className="text-center py-8">
            <Shield className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No Audit Logs Available</h3>
            <p className="text-gray-600">Security audit logging is not yet fully configured. Events will appear here once the audit log table is set up.</p>
          </CardContent>
        </Card>
      )}

      {auditLogs.length > 0 && (
        <>
          {/* Filters */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Filter Audit Logs</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-col md:flex-row gap-4">
                <div className="flex-1">
                  <Input
                    placeholder="Search by action, resource, or user ID..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>
                
                <div className="flex gap-2">
                  <Select value={filterAction} onValueChange={setFilterAction}>
                    <SelectTrigger className="w-48">
                      <SelectValue placeholder="Filter by action" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Actions</SelectItem>
                      <SelectItem value="login_attempt">Login Attempts</SelectItem>
                      <SelectItem value="referral_creation">Referral Creation</SelectItem>
                      <SelectItem value="permission_denied">Permission Denied</SelectItem>
                    </SelectContent>
                  </Select>
                  
                  <Select value={filterSuccess} onValueChange={setFilterSuccess}>
                    <SelectTrigger className="w-32">
                      <SelectValue placeholder="Status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All</SelectItem>
                      <SelectItem value="success">Success</SelectItem>
                      <SelectItem value="failure">Failure</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Audit Logs Table */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Security Events ({filteredLogs.length})</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {filteredLogs.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <Eye className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p>No audit logs found matching your criteria.</p>
                  </div>
                ) : (
                  filteredLogs.map((log) => (
                    <div key={log.id} className="border rounded-lg p-4 hover:bg-gray-50 transition-colors">
                      <div className="flex items-start justify-between">
                        <div className="flex items-start gap-3">
                          <div className="flex-shrink-0 mt-1">
                            {getActionIcon(log.action)}
                          </div>
                          
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <Badge variant={getActionColor(log.action, log.success)}>
                                {log.action.replace('_', ' ').toUpperCase()}
                              </Badge>
                              
                              <Badge variant={log.success ? 'default' : 'destructive'}>
                                {log.success ? 'SUCCESS' : 'FAILURE'}
                              </Badge>
                              
                              <span className="text-sm text-gray-600">
                                {log.resource_type}
                              </span>
                            </div>
                            
                            <div className="text-sm text-gray-600 space-y-1">
                              <div>User ID: <span className="font-mono text-xs">{log.user_id}</span></div>
                              
                              {log.resource_id && (
                                <div>Resource ID: <span className="font-mono text-xs">{log.resource_id}</span></div>
                              )}
                              
                              {log.error_message && (
                                <div className="text-red-600">Error: {log.error_message}</div>
                              )}
                              
                              {log.ip_address && (
                                <div>IP: <span className="font-mono text-xs">{log.ip_address}</span></div>
                              )}
                            </div>
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-2 text-sm text-gray-500">
                          <Calendar className="w-4 h-4" />
                          <span>{format(new Date(log.created_at), 'MMM dd, yyyy HH:mm:ss')}</span>
                        </div>
                      </div>
                      
                      {log.metadata && Object.keys(log.metadata).length > 0 && (
                        <div className="mt-3 pt-3 border-t">
                          <details className="text-sm">
                            <summary className="cursor-pointer text-gray-600 hover:text-gray-800">
                              View Metadata
                            </summary>
                            <pre className="mt-2 p-2 bg-gray-100 rounded text-xs overflow-x-auto">
                              {JSON.stringify(log.metadata, null, 2)}
                            </pre>
                          </details>
                        </div>
                      )}
                    </div>
                  ))
                )}
              </div>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
